import React, { useState, useContext } from 'react'
import { ThemeContext } from '../../context/ThemeContext.jsx'

/* ── SVG fallback (used when theme has no book images) ── */
function BookIcon({ filled, half, index }) {
  const clipId = `br-${index}`
  return (
    <svg width="18" height="24" viewBox="0 0 18 24" fill="none" style={{ display: 'block' }}>
      {half && (
        <defs>
          <clipPath id={clipId}>
            <rect x="0" y="0" width="9" height="24" />
          </clipPath>
        </defs>
      )}
      <rect x="0.75" y="0.75" width="16.5" height="22.5" rx="1.5"
        fill="rgba(0,0,0,0.05)" stroke="rgba(0,0,0,0.22)" strokeWidth="1.2" />
      <line x1="4.5" y1="0.75" x2="4.5" y2="23.25"
        stroke="rgba(0,0,0,0.15)" strokeWidth="1" />
      {(filled || half) && (
        <g clipPath={half ? `url(#${clipId})` : undefined}>
          <rect x="0.75" y="0.75" width="16.5" height="22.5" rx="1.5"
            fill="#2d2416" stroke="#2d2416" strokeWidth="1.2" />
          <rect x="0.75" y="0.75" width="4.5" height="22.5"
            fill="rgba(0,0,0,0.28)" />
          <line x1="5" y1="0.75" x2="5" y2="23.25"
            stroke="rgba(255,255,255,0.45)" strokeWidth="0.8" />
          <line x1="6.5" y1="5.5"  x2="15.5" y2="5.5"
            stroke="rgba(255,255,255,0.22)" strokeWidth="0.8" />
          <line x1="6.5" y1="9"    x2="15.5" y2="9"
            stroke="rgba(255,255,255,0.22)" strokeWidth="0.8" />
          <line x1="6.5" y1="12.5" x2="15.5" y2="12.5"
            stroke="rgba(255,255,255,0.22)" strokeWidth="0.8" />
        </g>
      )}
    </svg>
  )
}

/* ── Image-based book icon: open = full, closed = half or empty ── */
function BookIconImage({ filled, half, openImg, closedImg }) {
  const src     = filled ? openImg : closedImg
  const opacity = (filled || half) ? 1 : 0.18
  return (
    <img
      src={src}
      alt=""
      style={{ width: 36, height: 46, objectFit: 'contain', opacity, display: 'block' }}
    />
  )
}

export function BookRatingDisplay({ value }) {
  const { theme } = useContext(ThemeContext)
  if (!value) return null
  const full    = Math.floor(value)
  const hasHalf = (value % 1) >= 0.5
  const empty   = 5 - full - (hasHalf ? 1 : 0)

  if (theme.openBookImage) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
        {Array.from({ length: full }, (_, i) => (
          <BookIconImage key={`f${i}`} filled openImg={theme.openBookImage} closedImg={theme.closedBookImage} />
        ))}
        {hasHalf && (
          <BookIconImage key="h" half openImg={theme.openBookImage} closedImg={theme.closedBookImage} />
        )}
        {Array.from({ length: empty }, (_, i) => (
          <BookIconImage key={`e${i}`} openImg={theme.openBookImage} closedImg={theme.closedBookImage} />
        ))}
        <span style={{ fontSize: '0.72rem', color: 'var(--stone)', marginLeft: '5px', alignSelf: 'center' }}>
          {value}/5
        </span>
      </span>
    )
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
      {Array.from({ length: full  }, (_, i) => <BookIcon key={`f${i}`} filled index={`d-f${i}`} />)}
      {hasHalf && <BookIcon key="h" half index="d-h" />}
      {Array.from({ length: empty }, (_, i) => <BookIcon key={`e${i}`} index={`d-e${i}`} />)}
      <span style={{ fontSize: '0.72rem', color: 'var(--stone)', marginLeft: '5px', alignSelf: 'center' }}>
        {value}/5
      </span>
    </span>
  )
}

export default function BookRating({ value, onChange }) {
  const { theme } = useContext(ThemeContext)
  const [hovered, setHovered] = useState(null)
  const display = hovered !== null ? hovered : value

  return (
    <div className="book-rating-row" onMouseLeave={() => setHovered(null)}>
      {[1, 2, 3, 4, 5].map(n => {
        const isFull = display >= n
        const isHalf = !isFull && display >= n - 0.5
        return (
          <span key={n} className="book-rating-slot">
            <span
              className="book-rating-zone"
              style={{ left: 0 }}
              onMouseEnter={() => setHovered(n - 0.5)}
              onClick={() => onChange(value === n - 0.5 ? 0 : n - 0.5)}
            />
            <span
              className="book-rating-zone"
              style={{ left: '50%' }}
              onMouseEnter={() => setHovered(n)}
              onClick={() => onChange(value === n ? 0 : n)}
            />
            {theme.openBookImage ? (
              <BookIconImage
                filled={isFull}
                half={isHalf}
                openImg={theme.openBookImage}
                closedImg={theme.closedBookImage}
              />
            ) : (
              <BookIcon filled={isFull} half={isHalf} index={n} />
            )}
          </span>
        )
      })}
      {display > 0 && (
        <span style={{ fontSize: '0.78rem', color: 'var(--stone)', marginLeft: '6px', alignSelf: 'center' }}>
          {display}/5
        </span>
      )}
    </div>
  )
}
